import { FunctionalComponent } from 'preact'
import type { AudienceView } from '@/utils/leadAudienceFilter'
import { NonProspectSwitch } from './NonProspectSwitch'

interface AudienceViewToggleProps {
  /** Required unless `hideAudienceViews` — there is no selector to reflect without it. */
  view?: AudienceView
  onChange?: (view: AudienceView) => void
  showNonProspects: boolean
  onToggleNonProspects: () => void
  /** How many rows in scope are non-prospects, so the switch can say what it would reveal. */
  nonProspectCount?: number
  /**
   * Hides the three-state audience selector, leaving only the non-prospect switch. For screens where
   * audience membership is not a question that can be asked: every row of a file import IS the
   * audience, so the selector there would put 100% under "Audience" and nothing under the other two.
   */
  hideAudienceViews?: boolean
  class?: string
}

const VIEW_OPTIONS: Array<{ key: AudienceView; label: string; title: string }> = [
  { key: 'audience', label: 'Audience', title: 'People this campaign targeted' },
  { key: 'nonAudience', label: 'Non-Audience', title: 'People who came to us without being targeted' },
  { key: 'all', label: 'All', title: 'Both, together' },
]

/**
 * The audience selector (ORB-619): a true three-state control where Non-Audience is the complement
 * of Audience rather than "everything", plus a separate switch for non-prospects.
 *
 * Two controls, not one, because audience membership and prospect status are independent axes —
 * see `leadAudienceFilter`. Radiogroup semantics match the sibling Contacts/Companies switch.
 */
export const AudienceViewToggle: FunctionalComponent<AudienceViewToggleProps> = ({
  view,
  onChange,
  showNonProspects,
  onToggleNonProspects,
  nonProspectCount,
  hideAudienceViews = false,
  class: className = '',
}) => {
  // Showing non-prospects is a mode, not an addition: it shows only those people, so the audience
  // axis has nothing left to slice and the selector is locked while it is on.
  const audienceDisabled = showNonProspects

  return (
    <div class={`inline-flex flex-wrap items-center gap-2 ${className}`.trim()}>
      {!hideAudienceViews && (
      <div
        role="radiogroup"
        aria-label="Audience view"
        aria-disabled={audienceDisabled}
        title={audienceDisabled ? 'Showing non-prospects only — turn the switch off to filter by audience' : undefined}
        class={`inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden ${
          audienceDisabled ? 'opacity-40' : ''
        }`}
      >
        {VIEW_OPTIONS.map((option) => {
          const active = option.key === view
          return (
            <button
              key={option.key}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={audienceDisabled}
              title={audienceDisabled ? undefined : option.title}
              onClick={() => !active && !audienceDisabled && onChange?.(option.key)}
              class={`px-3 py-1.5 text-xs font-medium transition focus:outline-none focus:z-10 ${
                active
                  ? 'bg-green-600 text-white dark:bg-green-500'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {option.label}
            </button>
          )
        })}
      </div>
      )}

      <NonProspectSwitch
        showNonProspects={showNonProspects}
        onToggle={onToggleNonProspects}
        nonProspectCount={nonProspectCount}
      />
    </div>
  )
}

export default AudienceViewToggle
